import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  Container
} from '@mui/material';
import { Verified, GppBad, HelpOutline } from '@mui/icons-material';
import {
  verifyCertificate,
  CertificateVerification
} from '../services/certificateService';

/**
 * Public certificate verification (part of feature #04).
 *
 * Deliberately unauthenticated: the audience is a school admissions officer holding a
 * printed certificate, who has no account and never will. Requiring a login would make
 * the certificate unverifiable by exactly the people it is meant to convince.
 *
 * It shows only what the printed certificate already shows — name, series, score, date.
 * The server sends no studentId, phone or sessionId to this surface, so a stranger with
 * the code learns nothing they were not already holding.
 */

const VerifyCertificatePage: React.FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const [result, setResult] = useState<CertificateVerification | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!hash) {
      setLoading(false);
      return undefined;
    }
    verifyCertificate(hash).then((v) => {
      if (cancelled) return;
      setResult(v);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [hash]);

  const verdict = (() => {
    if (!result) {
      return {
        icon: <HelpOutline sx={{ fontSize: 48 }} />,
        color: 'text.secondary',
        title: 'Could not check this certificate',
        detail: 'Something went wrong reaching the verification service. Please try again.'
      };
    }
    switch (result.status) {
      case 'valid':
        return {
          icon: <Verified sx={{ fontSize: 48 }} />,
          color: 'success.main',
          title: 'Valid certificate',
          detail: 'This certificate was issued by NextZenOlympiad and has not been revoked.'
        };
      case 'revoked':
        return {
          icon: <GppBad sx={{ fontSize: 48 }} />,
          color: 'error.main',
          title: 'Revoked certificate',
          detail: 'This certificate was issued but has since been revoked. Treat it as invalid.'
        };
      default:
        return {
          icon: <GppBad sx={{ fontSize: 48 }} />,
          color: 'error.main',
          title: 'No matching certificate',
          detail: 'No certificate matches this code. Check the code, or treat it as invalid.'
        };
    }
  })();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 6 }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src="/liveolympiad.svg" alt="NextZenOlympiad" style={{ height: 56 }} />
        </Box>

        <Paper sx={{ p: 4, borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box sx={{ color: verdict.color }}>{verdict.icon}</Box>
                <Typography variant="h5" sx={{ mt: 1 }}>
                  {verdict.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {verdict.detail}
                </Typography>
              </Box>

              {result && result.status !== 'not_found' && (
                <>
                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ display: 'grid', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Awarded to
                      </Typography>
                      <Typography variant="h6">{result.studentName ?? '—'}</Typography>
                    </Box>

                    {result.series && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Assessment
                        </Typography>
                        <Typography variant="body1">{result.series}</Typography>
                      </Box>
                    )}

                    {result.percentage != null && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Score
                        </Typography>
                        <Typography variant="body1">
                          {result.percentage.toFixed(1)}%
                          {result.score != null && result.totalScore != null && (
                            <> ({result.score}/{result.totalScore})</>
                          )}
                        </Typography>
                      </Box>
                    )}

                    {result.issuedAt && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Issued
                        </Typography>
                        <Typography variant="body1">
                          {new Date(result.issuedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {result.status === 'revoked' && (
                    <Chip color="error" label="Revoked" sx={{ mt: 3 }} />
                  )}
                </>
              )}
            </>
          )}
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          Verification code: {hash ?? '—'}
        </Typography>
      </Container>
    </Box>
  );
};

export default VerifyCertificatePage;
